/* eslint-disable max-len */

import { Arr, Burden } from '@deqo/foundation';
import { ModelSnapshot } from '@deqo/podopolis';
import { uuid, waitFor } from '../../../CodesThatsGottaGoSomewhereElse';
import { BoardData, BoardParticipantData } from '../../../api/ApiTypes';
import { ProfileData } from '../../../api/endpoints/me';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ClientManager, Communicator, ObjectManager } from '../../../concepts/objects';
import { RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { TopicManager } from '../../../concepts/objects/TopicManager';
import { HandwritingFontList } from '../../../platform/assets/InAppFontList';
import { ColorList } from '../../../ui';
import { DefaultPackageHost, PackageType, Resolver } from '../../Container';
import { Packages } from '../../Packages';
import { PassportModel } from '../../PassportModel';
import { createScope } from '../../Scope';
import { BoardUsageModel } from '../../account/BoardUsageModel';
import { CorePackage } from '../CorePackage';
import { EditorOptions } from '../EditorOptions';
import { EditorPodObjectManagerBackend } from '../EditorPodObjectManagerBackend';
import { EditorPodSession } from '../EditorPodSession';
import { MetadataProvider } from '../MetadataProvider';
import { HistoryPackage } from '../history/HistoryPackage';
import { ToolingPackage } from '../tooling/ToolingPackage';
import { kioskPlayRecording, kioskStartRecording, kioskStopRecording, kioskToggleRecording } from './KioskCommands';
import { KioskController } from './KioskController';
import { KioskUser } from './KioskUser';

const defaultSnapshot: ModelSnapshot = {
    instances: {
        root: {
            type: 'root',
            variant: 'component'
        }
    },
    childLinks: {
        root: {},
    },
    parentLinks: {
    },
    states: {
        root: {}
    },
    version: 0
};

const defaultBoardData: any = {
    id: 'BO0000KIOSK',
    name: 'Kiosk',
    label: 'Kiosk',
    type: 1,
    userId: 'US0000KIOSK',
    podId: '',
    password: false,
    viewers: 'workspace',
    guests: 'deny',
    createdAt: '2022-09-28T12:10:07.484Z',
    updatedAt: '2022-09-28T12:10:07.484Z',
    accountId: 'AC0000KIOSK',
    workspaceId: null,
    folderId: null
};

const defaultProfileData: ProfileData = {
    id: 'US0000KIOSK',
    legacyId: null,
    type: 1,
    name: 'Kiosk User',
    shortName: 'Kiosk',
    initials: 'KU',
    email: 'kiosk@metroretro.io',
    emailSubscribed: false,
    emailVerified: true,
    photo: null,
    external: false,
    color: Arr.random(ColorList),
    handwriting: Arr.random(HandwritingFontList).name,
    accounts: [],
    connections: [],
    credentials: [],
    integrations: { calendar: false },
    createdAt: new Date(),
};

const defaultPermissions = [
    'base:read',
    'base:write',
    'ac_billing:read',
    'ac_billing:write',
    'ac_team:read',
    'ac_team:write',
    'bo:facilitate',
    'bo:read',
    'bo:write',
    'da:read',
    'da:write',
    'wo:read',
    'wo:write'
];

const defaultProps = {
    board: defaultBoardData,
    user: defaultProfileData,
    snapshot: defaultSnapshot,
    permissions: defaultPermissions,
    packages: [],
};

class KioskMetadataProvider {

    constructor(private me: ProfileData, private board: BoardData) {
    }

    public fetchBoardData(): Promise<BoardData> {
        return Promise.resolve(this.board);
    }

    public fetchProfileData(): Promise<ProfileData> {
        return Promise.resolve({ flags: {}, ...this.me });
    }

    public fetchParticipantData(): Promise<BoardParticipantData[]>;
    public fetchParticipantData(userId: string): Promise<BoardParticipantData | null>;
    public fetchParticipantData(userId?: string): Promise<any> {
        if (!userId) return Promise.resolve([this.me]);
        return Promise.resolve(KioskUser.get(userId));
    }
}

export interface KioskScopeProps {
    board?: BoardData;
    user?: ProfileData;
    snapshot?: ModelSnapshot;
    permissions?: string[];
    packages?: PackageType[];
    options?: EditorOptions;
};

export const KioskEditorScope = createScope<KioskScopeProps>({
    async refresh(p, c) {
        p = { ...defaultProps, ...p };

        const user = KioskUser.me;

        const ts = performance.now();
        const trash = [] as Burden[];

        const resolver = c.get(Resolver);

        const te = performance.now() - ts;
        if (te < 500) {
            await waitFor(500 - te);
        }

        const ctrl = new KioskController({ time: 0, ...p.snapshot }, user);

        const podSession = new EditorPodSession(
            ctrl.createPod(),
        );

        await podSession.start();

        const backend = new EditorPodObjectManagerBackend(uuid(), podSession, () => true);
        const objects = new ObjectManager(resolver, backend);
        const clients = new ClientManager(podSession as any);
        const comms = new Communicator(podSession as any, clients);
        const topics = new TopicManager(podSession as any);
        const recordSets = new RecordSetManager(podSession);

        c.bind(BoardUsageModel).toConstantValue(BoardUsageModel.create({
            active: 0,
            boards: [],
            limit: 0,
        }));

        c.bind(EditorPodSession).toConstantValue(podSession);
        c.bind(ClientManager).toConstantValue(clients);
        c.bind(Communicator).toConstantValue(comms);
        c.bind(TopicManager).toConstantValue(topics);
        c.bind(RecordSetManager).toConstantValue(recordSets);
        c.bind(MetadataProvider).toConstantValue(new KioskMetadataProvider(p.user, p.board) as any);
        c.bind(ObjectManager).toConstantValue(objects);
        c.bind(EditorOptions).toConstantValue({ kiosk: true, ui: false, ...(p.options || {}) });
        c.bind(KioskController).toConstantValue(ctrl);
        c.bind(PassportModel).toConstantValue(PassportModel.create(p.permissions));

        trash.push(objects, clients, comms);

        const host = new DefaultPackageHost(c);

        const basePackages: PackageType[] = [
            CorePackage,
            HistoryPackage,
            ToolingPackage,
        ];

        host.plug(inCommands(
            kioskStartRecording,
            kioskStopRecording,
            kioskToggleRecording,
            kioskPlayRecording,
        ));

        const allPackages = basePackages.concat(Packages).concat(p.packages ?? []);
        allPackages.forEach(t => (new t()).install(host));

        await host.go();
        trash.push(host);

        return trash;
    }
});
